import { useEffect, useState } from "react";
import "./AirportWeather.css";

const COMPASS = [
  "north",
  "north-east",
  "east",
  "south-east",
  "south",
  "south-west",
  "west",
  "north-west",
];

function compassWord(deg) {
  if (deg == null || Number.isNaN(Number(deg))) return null;
  const i = Math.round(Number(deg) / 45) % 8;
  return COMPASS[(i + 8) % 8];
}

function windText(dir, kt) {
  if (kt == null || Number(kt) === 0) return "Calm — barely any wind";
  const from = compassWord(dir);
  const speed = `${Math.round(Number(kt))} kn`;
  return from ? `Wind ${speed} from the ${from}` : `Wind ${speed}`;
}

const COVER_WORDS = {
  SKC: "clear skies",
  CLR: "clear skies",
  NSC: "clear skies",
  NCD: "clear skies",
  FEW: "a few clouds",
  SCT: "scattered clouds",
  BKN: "broken clouds",
  OVC: "overcast",
};

function skyText(clouds) {
  if (!clouds || clouds.length === 0) return "Clear skies";
  // Report the most significant (last-listed / highest cover) layer in words.
  const order = ["FEW", "SCT", "BKN", "OVC"];
  let best = null;
  let bestRank = -1;
  for (const c of clouds) {
    const rank = order.indexOf(c.cover);
    if (rank > bestRank) {
      bestRank = rank;
      best = c.cover;
    }
  }
  const word = COVER_WORDS[best];
  if (word) return word.charAt(0).toUpperCase() + word.slice(1);
  return "Clear skies";
}

function visibilityText(v) {
  if (v == null || v === "") return null;
  const num = Number(v);
  if (!Number.isNaN(num)) {
    if (num >= 6) return "Visibility: good (6+ miles)";
    return `Visibility: about ${num} miles`;
  }
  // Non-numeric values like "10+" come through as strings.
  return `Visibility: ${v} miles`;
}

export default function AirportWeather({ icao, label }) {
  const [weather, setWeather] = useState(null);
  const valid = typeof icao === "string" && /^[A-Za-z]{4}$/.test(icao);

  useEffect(() => {
    if (!valid) {
      setWeather(null);
      return;
    }
    const controller = new AbortController();
    setWeather(null);
    fetch(`/api/metar/${icao.toUpperCase()}`, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.weather) setWeather(data.weather);
      })
      .catch(() => {
        /* aborted or offline — stay quiet */
      });
    return () => controller.abort();
  }, [icao, valid]);

  if (!valid || !weather) return null;

  const temp = weather.tempC != null ? `${Math.round(Number(weather.tempC))}°C` : null;
  const vis = visibilityText(weather.visibility);

  return (
    <div className="airport-weather">
      {label ? <div className="aw-label">{label}</div> : null}
      {temp ? <div className="aw-temp">{temp}</div> : null}
      <div className="aw-line">{windText(weather.windDir, weather.windKt)}</div>
      <div className="aw-line">{skyText(weather.clouds)}</div>
      {weather.conditions ? (
        <div className="aw-line">Conditions: {weather.conditions}</div>
      ) : null}
      {vis ? <div className="aw-line">{vis}</div> : null}
    </div>
  );
}
